export const NODE_W = 320;
export const NODE_H = 150;
const GAP_X = 72;
const GAP_Y = 60;
const PAD = 48;

export function isVerticalFlowType(type) {
  const t = String(type || '');
  return t.startsWith('agent/') || t.startsWith('workflow/');
}

export function shouldLayoutTopDown(packages) {
  return (packages || []).some((pkg) => isVerticalFlowType(pkg.type));
}

function unique(ids) {
  return [...new Set(ids)];
}

/**
 * Longest-path layering + barycenter order. Positions are React Flow top-left.
 * Top-down: layer increases Y. Left-right: layer increases X.
 */
export function layoutDag(packages, connections, { vertical = true } = {}) {
  const ids = (packages || []).map((pkg) => pkg.id);
  const idSet = new Set(ids);
  const incoming = new Map(ids.map((id) => [id, []]));

  for (const conn of connections || []) {
    const src = conn.source_package_id;
    const tgt = conn.target_package_id;
    if (!idSet.has(src) || !idSet.has(tgt) || src === tgt) continue;
    incoming.get(tgt).push(src);
  }

  const layerOf = new Map();
  const visit = (id, stack) => {
    if (layerOf.has(id)) return layerOf.get(id);
    if (stack.has(id)) return 0;
    stack.add(id);
    const parents = unique(incoming.get(id) || []);
    const layer =
      parents.length === 0
        ? 0
        : Math.max(...parents.map((p) => visit(p, stack))) + 1;
    stack.delete(id);
    layerOf.set(id, layer);
    return layer;
  };
  ids.forEach((id) => visit(id, new Set()));

  const byLayer = [];
  for (const id of ids) {
    const layer = layerOf.get(id) || 0;
    if (!byLayer[layer]) byLayer[layer] = [];
    byLayer[layer].push(id);
  }

  const order = new Map();
  byLayer.forEach((row, layerIdx) => {
    if (layerIdx > 0) {
      row.sort((a, b) => {
        const bary = (id) => {
          const parents = unique(incoming.get(id) || []);
          if (!parents.length) return 0;
          return (
            parents.reduce((sum, p) => sum + (order.get(p) ?? 0), 0) /
            parents.length
          );
        };
        return bary(a) - bary(b) || String(a).localeCompare(String(b));
      });
    }
    row.forEach((id, i) => order.set(id, i));
  });

  const positions = new Map();
  byLayer.forEach((row, layerIdx) => {
    const span = row.length * NODE_W + Math.max(0, row.length - 1) * GAP_X;
    row.forEach((id, i) => {
      const along = i * (NODE_W + GAP_X) - span / 2;
      const down = layerIdx * (NODE_H + GAP_Y);
      if (vertical) {
        positions.set(id, { x: along, y: down });
      } else {
        positions.set(id, { x: down, y: along });
      }
    });
  });

  let minX = Infinity;
  let minY = Infinity;
  for (const pos of positions.values()) {
    minX = Math.min(minX, pos.x);
    minY = Math.min(minY, pos.y);
  }
  if (!Number.isFinite(minX)) return positions;
  for (const [id, pos] of positions) {
    positions.set(id, {
      x: pos.x - minX + PAD,
      y: pos.y - minY + PAD,
    });
  }
  return positions;
}
